import { Glue42 } from "../glue";

export default class Utils {
    public static getGDMajorVersion() {
        if (typeof window === "undefined") {
            return -1;
        }
        if (!window.glueDesktop) {
            return -1;
        }
        if (!window.glueDesktop.version) {
            return -1;
        }
        const ver = Number(window.glueDesktop.version.substr(0, 1));
        return isNaN(ver) ? -1 : ver;
    }

    public static callbackifyPromise<T>(
        action: () => Promise<T>,
        successCallback?: (args?: T) => void,
        errorCallback?: (errorMessage: string) => void): Promise<T> {

        const fail = (error: any) => {
            let msg = error;
            if (error instanceof Error) {
                msg = error.message;
            }
            if (typeof errorCallback === "function") {
                errorCallback(msg);
                return;
            }

            return Promise.reject(msg);
        };

        try {
            return action()
                .then((result) => {
                    if (typeof successCallback === "function") {
                        successCallback(result);
                    }
                    return result;
                })
                .catch((error) => {
                    return fail(error);
                });
        } catch (err) {
            return fail(err);
        }
    }

    public static getMonitor(bounds: Glue42.Windows.Bounds, displays?: Glue42.Screen[]): Glue42.Screen {
        const monitorsSortedByOverlap = displays.map((m) => {
            const { left, top, workingAreaWidth: width, workingAreaHeight: height } = m;
            const overlap = this.calculateTotalOverlap({ left, top, width, height }, bounds);
            return {
                monitor: m,
                totalOverlap: overlap
            };
        }).sort((a, b) => b.totalOverlap - a.totalOverlap);

        return monitorsSortedByOverlap[0].monitor;
    }

    public static getDisplayCenterOfScreen(a: Partial<Glue42.Windows.Bounds>, currentDisplay: Glue42.Displays.Display, primaryDisplay: Glue42.Displays.Display) {
        const physicalWidth = a.width / currentDisplay.scaleFactor;
        const physicalHeight = a.height / currentDisplay.scaleFactor;
        const physicalDisplayLeft = currentDisplay.workArea.left / primaryDisplay.scaleFactor;
        const physicalDisplayTop = currentDisplay.workArea.top / primaryDisplay.scaleFactor;
        const physicalDisplayWidth = currentDisplay.workArea.width / currentDisplay.scaleFactor;
        const physicalDisplayHeight = currentDisplay.workArea.height / currentDisplay.scaleFactor;

        const physicalHOffset = Math.max((physicalDisplayWidth - physicalWidth) / 2, 0);
        const physicalVOffset = Math.max((physicalDisplayHeight - physicalHeight) / 2, 0);
        const centeredPhysicalLeft = Math.floor(physicalDisplayLeft + physicalHOffset);
        const centeredPhysicalTop = Math.floor(physicalDisplayTop + physicalVOffset);
        const left = centeredPhysicalLeft * primaryDisplay.scaleFactor;
        const top = centeredPhysicalTop * primaryDisplay.scaleFactor;

        return {
            left,
            top,
            width: a.width,
            height: a.height
        };
    }

    public static isNode() {
        if (typeof Utils._isNode !== "undefined") {
            return Utils._isNode;
        }

        if (typeof window !== "undefined") {
            Utils._isNode = false;
            return false;
        }

        // Only Node.JS has a process variable that is of [[Class]] process
        try {
            Utils._isNode = Object.prototype.toString.call(global.process) === "[object process]";
        } catch (e) {
            Utils._isNode = false;
        }
        return Utils._isNode;
    }

    private static _isNode?: boolean;

    private static calculateTotalOverlap(r1: Glue42.Windows.Bounds, r2: Glue42.Windows.Bounds) {
        const r1x = r1.left;
        const r1y = r1.top;
        const r1xMax = r1x + r1.width;
        const r1yMax = r1y + r1.height;

        const r2x = r2.left;
        const r2y = r2.top;
        const r2xMax = r2x + r2.width;
        const r2yMax = r2y + r2.height;

        const xOverlap = Math.max(0, Math.min(r1xMax, r2xMax) - Math.max(r1x, r2x));
        const yOverlap = Math.max(0, Math.min(r1yMax, r2yMax) - Math.max(r1y, r2y));

        return xOverlap * yOverlap;
    }
}
